import { useState, useRef, useEffect, useCallback } from 'react'

export type ReadingFile = {
  state: 'READING'
  size: number
  name: string
  type: string
}

export type CompletedFile = {
  state: 'COMPLETED'
  name: string
  size: number
  type: string
  content: string
}

export type CustomFile = ReadingFile | CompletedFile

export interface FileManagerProps {
  validateFile?: (file: File) => {
    valid: boolean
    reason?: string
  }
}

export default function useFileManager(props: FileManagerProps = {}): {
  files: CustomFile[]
  registerFile: (file: File) => Promise<CustomFile[]>
  unregisterFile: (file: CustomFile) => Promise<CustomFile[]>
} {
  const { validateFile } = props
  const [files, updateFiles] = useState<CustomFile[]>([])

  // We need to keep track on whether the component is mounted or not
  const isUnmounted = useRef<boolean>(false)
  useEffect(() => {
    return () => {
      isUnmounted.current = true
    }
  }, [isUnmounted])

  const handleFileAddition = useCallback(
    (file: File) => {
      return new Promise<CustomFile[]>((resolve, reject) => {
        const fileReference: ReadingFile = {
          state: 'READING',
          size: file.size,
          name: file.name,
          type: file.type,
        }
        // Reject file if validation fails
        if (typeof validateFile === 'function') {
          const validation = validateFile(file)
          if (validation.valid === false) {
            return reject(validation.reason)
          }
        }
        // Update the files with a "READING" file
        updateFiles(latestFiles => [...latestFiles, fileReference])
        const fileReader = new FileReader()
        fileReader.addEventListener('load', () => {
          // If the component is not mounted, then we should abort
          if (isUnmounted.current) {
            return
          }
          // Update the referenced referenced file with a COMPLETED-state and it's content
          updateFiles(latestFiles => {
            const updatedFiles = [...latestFiles]
            if (fileReader.result) {
              const idx = latestFiles.findIndex(file => file === fileReference)
              updatedFiles[idx] = {
                state: 'COMPLETED',
                name: fileReference.name,
                size: fileReference.size,
                type: fileReference.type,
                // Adds the Base64 content
                content: fileReader.result.toString(),
              }
            }
            // Resolve the promise with the latest files
            resolve(updatedFiles)
            // Update state
            return updatedFiles
          })
        })
        // Throw error if file couldn't be read.
        fileReader.addEventListener('error', () => {
          console.error(`Error occurred reading file: ${fileReference.name}`)
          reject(`Unable to read file: ${fileReference.name}`)
        })

        // Read the file as Base64
        fileReader.readAsDataURL(file)
      })
    },
    [updateFiles, validateFile],
  )

  const handleFileRemoval = useCallback(
    (file: CustomFile) => {
      return new Promise<CustomFile[]>(resolve => {
        updateFiles(latestFiles => {
          // Remove the file from the list of files
          const updatedFiles = latestFiles.filter(x => x !== file)

          // Resolve the promise with the latest files
          resolve(updatedFiles)

          // Update state
          return updatedFiles
        })
      })
    },
    [updateFiles],
  )

  return {
    files,
    registerFile: handleFileAddition,
    unregisterFile: handleFileRemoval,
  }
}
